/**
 * `<cloud-zoom>` renders a CloudZoom-style hover magnifier for the product
 * image it wraps: a lens follows the cursor over the image, and the
 * magnified portion is rendered in a panel docked beside the image.
 *
 * Only active on hover-capable, fine-pointer devices (desktop). On
 * touch/coarse-pointer devices this element is a no-op passthrough so the
 * existing tap-to-open full screen zoom dialog keeps working.
 *
 * All critical visibility/positioning styles for the panel are set
 * inline rather than relying on an external stylesheet, since the panel
 * is created dynamically and appended outside of this element.
 */
class CloudZoom extends HTMLElement {
  /** Magnification multiplier applied to the lens area inside the panel. */
  static ZOOM_LEVEL = 3;
  /** Horizontal gap (px) between the image and the zoom panel. */
  static PANEL_GAP = 40;
  /** Minimum gap (px) kept between the panel and the viewport's top/bottom edges. */
  static VIEWPORT_BUFFER = 16;
  /** Highest possible z-index, to guarantee the panel renders on top. */
  static PANEL_Z_INDEX = 2147483647;
  /**
   * Min viewport width (px) for the hover magnifier - matches the theme's
   * mobile/desktop breakpoint. Below this the mobile carousel is shown and
   * hover-zoom must never run, regardless of what the device reports about
   * pointer capabilities (e.g. desktop devtools emulating a phone).
   */
  static DESKTOP_MIN_WIDTH = 750;

  connectedCallback() {
    /** @type {HTMLImageElement | null} */
    this.image = this.querySelector('img.product-media__image');

    if (!this.image) return;

    this.lens = null;
    this.panel = null;
    this.panelImage = null;

    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleReposition = this.handleReposition.bind(this);

    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.destroyZoom();
  }

  get isMagnifierEnabled() {
    return window.matchMedia(
      `(hover: hover) and (pointer: fine) and (min-width: ${CloudZoom.DESKTOP_MIN_WIDTH}px)`
    ).matches;
  }

  /**
   * The panel is always sized to match the gallery's primary (first) image,
   * so smaller secondary/grid images don't produce a shrunken preview.
   */
  getReferenceRect() {
    const gallery = this.closest('media-gallery');

    // The gallery renders both the slideshow and grid markup at once and
    // toggles which is visible per breakpoint with `display: none`, so the
    // first `.cloud-zoom` in DOM order isn't necessarily the one on screen
    // (a hidden element's rect is all zeros). Use the first one that's
    // actually visible/sized.
    if (gallery) {
      for (const candidate of gallery.querySelectorAll('.cloud-zoom')) {
        const rect = candidate.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) return rect;
      }
    }

    return this.getBoundingClientRect();
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerEnter(event) {
    if (event.pointerType !== 'mouse' || !this.isMagnifierEnabled || !this.image) return;

    this.createLens();
    this.createPanel();
    window.addEventListener('scroll', this.handleReposition, { passive: true });
    window.addEventListener('resize', this.handleReposition);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (!this.lens || !this.panel || !this.panelImage) return;

    // Rects are measured in positionPanel (on open, scroll and resize), so a
    // move only writes transforms and never forces a layout.
    if (!this._rect) this.positionPanel();
    const rect = this._rect;
    const referenceRect = this._refRect;
    if (!rect || !referenceRect) return;
    const lensFraction = 1 / CloudZoom.ZOOM_LEVEL;

    let lensXFraction = (event.clientX - rect.left) / rect.width - lensFraction / 2;
    let lensYFraction = (event.clientY - rect.top) / rect.height - lensFraction / 2;

    lensXFraction = Math.max(0, Math.min(lensXFraction, 1 - lensFraction));
    lensYFraction = Math.max(0, Math.min(lensYFraction, 1 - lensFraction));

    this.lens.style.transform = `translate(${lensXFraction * rect.width}px, ${lensYFraction * rect.height}px)`;

    const panelImageWidth = referenceRect.width * CloudZoom.ZOOM_LEVEL;
    const panelImageHeight = referenceRect.height * CloudZoom.ZOOM_LEVEL;

    this.panelImage.style.transform = `translate(${-lensXFraction * panelImageWidth}px, ${-lensYFraction * panelImageHeight}px)`;
  }

  handlePointerLeave() {
    this.destroyZoom();
  }

  handleReposition() {
    if (this._repositionFrame) return;
    this._repositionFrame = requestAnimationFrame(() => {
      this._repositionFrame = 0;
      this.positionPanel();
    });
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    // Only suppress the tap-to-open zoom dialog when the hover magnifier is
    // actually showing (a real mouse hover created the panel). Gating on
    // device capability alone would also swallow genuine taps on touch
    // devices that report as hover-capable (touchscreen laptops, some
    // hybrid/tablet devices), leaving the image feeling unresponsive.
    if (this.panel) {
      event.preventDefault();
    }
  }

  createLens() {
    if (this.lens) return;

    this.lens = document.createElement('div');
    this.lens.className = 'cloud-zoom__lens';
    this.appendChild(this.lens);
  }

  createPanel() {
    if (this.panel || !this.image) return;

    const highResUrl = this.image.getAttribute('data_max_resolution') || this.image.currentSrc || this.image.src;

    this.panel = document.createElement('div');
    Object.assign(this.panel.style, {
      position: 'fixed',
      zIndex: String(CloudZoom.PANEL_Z_INDEX),
      overflow: 'hidden',
      backgroundColor: '#fff',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: '4px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
      pointerEvents: 'none',
      opacity: '0',
      transform: 'translateX(-8px)',
      transition: 'opacity 200ms ease-out, transform 200ms ease-out',
    });

    // A real <img> (rather than a background-image div) lets the browser
    // use its native image-scaling pipeline for the magnified view, and
    // keeps every pointermove update to a compositor-only `transform`
    // instead of also writing `width`/`height` on each move.
    this.panelImage = document.createElement('img');
    this.panelImage.src = highResUrl;
    this.panelImage.alt = '';
    Object.assign(this.panelImage.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      maxWidth: 'none',
      transformOrigin: '0 0',
      transition: 'transform 80ms ease-out',
    });
    this.panel.appendChild(this.panelImage);

    // Appended directly to the body: the panel's positioning and visual
    // styles are all set inline above, so it doesn't depend on this
    // theme's component-scoped `{% stylesheet %}` rules, and `position:
    // fixed` means it renders relative to the viewport regardless of
    // where it sits in the DOM.
    document.body.appendChild(this.panel);
    this.positionPanel();

    // Fade and slide the panel in on its own animation frame so the
    // transition actually plays instead of starting in its end state.
    requestAnimationFrame(() => {
      if (!this.panel) return;
      this.panel.style.opacity = '1';
      this.panel.style.transform = 'translateX(0)';
    });
    requestAnimationFrame(() => {
      this.lens?.classList.add('cloud-zoom__lens--visible');
    });
  }

  positionPanel() {
    if (!this.panel) return;

    const rect = this.getBoundingClientRect();
    const referenceRect = this.getReferenceRect();
    this._rect = rect;
    this._refRect = referenceRect;

    if (this.lens) {
      const lensFraction = 1 / CloudZoom.ZOOM_LEVEL;
      this.lens.style.width = `${rect.width * lensFraction}px`;
      this.lens.style.height = `${rect.height * lensFraction}px`;
    }

    const panelWidth = referenceRect.width;
    const panelHeight = referenceRect.height;

    this.panel.style.width = `${panelWidth}px`;
    this.panel.style.height = `${panelHeight}px`;

    if (this.panelImage) {
      this.panelImage.style.width = `${panelWidth * CloudZoom.ZOOM_LEVEL}px`;
      this.panelImage.style.height = `${panelHeight * CloudZoom.ZOOM_LEVEL}px`;
    }

    // Keep the panel aligned with the hovered image's top edge, but never
    // let it run off the top or bottom of the viewport (e.g. for images
    // near the bottom of a tall grid) - that reads as a glitch rather than
    // an intentional preview.
    const buffer = CloudZoom.VIEWPORT_BUFFER;
    const maxTop = Math.max(buffer, window.innerHeight - panelHeight - buffer);
    const top = Math.min(Math.max(rect.top, buffer), maxTop);

    this.panel.style.top = `${top}px`;

    // Prefer docking to the right of the image, but if there isn't room,
    // clamp within the viewport rather than falling back to a position
    // that can land on top of the image itself (e.g. when the panel is as
    // wide as the image and there's no room on either side).
    const left = Math.max(rect.right, referenceRect.right) + CloudZoom.PANEL_GAP;
    const maxLeft = Math.max(0, document.documentElement.clientWidth - panelWidth);

    this.panel.style.left = `${Math.min(left, maxLeft)}px`;
  }

  destroyZoom() {
    window.removeEventListener('scroll', this.handleReposition);
    window.removeEventListener('resize', this.handleReposition);
    this.lens?.remove();
    this.lens = null;
    this.panel?.remove();
    this.panel = null;
    this.panelImage = null;
    this._rect = null;
    this._refRect = null;
    if (this._repositionFrame) cancelAnimationFrame(this._repositionFrame);
    this._repositionFrame = 0;
  }
}

if (!customElements.get('cloud-zoom')) {
  customElements.define('cloud-zoom', CloudZoom);
}